<template>
  <div class="plan-page">
    <div class="container py-5">
      <!-- Top hero -->
      <div class="hero-card mb-4">
        <div
          class="d-flex flex-wrap align-items-start justify-content-between gap-3"
        >
          <div class="pe-lg-4">
            <div class="stepper mb-2">
              <span class="step done">
                <i class="ti ti-check"></i> Account created
              </span>
              <span class="step active">
                <i class="ti ti-crown"></i> Choose plan
              </span>
              <span class="step">
                <i class="ti ti-layout-dashboard"></i> Dashboard
              </span>
            </div>

            <h2 class="fw-bold mb-2">Choose a plan for your company</h2>
            <p class="text-muted mb-0">
              You’ll start with <span class="fw-semibold">trial access</span>.
              Pick a plan now to unlock the right modules for your team.
            </p>

            <div class="hero-badges mt-3">
              <span class="pill"
                ><i class="ti ti-shield-check me-1"></i> Secure</span
              >
              <span class="pill"
                ><i class="ti ti-file-certificate me-1"></i>
                Compliance-ready</span
              >
              <span class="pill"
                ><i class="ti ti-refresh me-1"></i> Upgrade anytime</span
              >
            </div>
          </div>

          <div class="text-lg-end">
            <!-- <router-link
              to="/dashboard/admin-dashboard"
              class="btn btn-outline-secondary btn-sm"
            >
              Skip for now
            </router-link> -->
            <!-- <router-link
              :to="safeDashboard"
              class="btn btn-outline-secondary btn-sm"
            >
              Skip for now
            </router-link> -->
          </div>
        </div>

        <!-- Controls bar -->
        <div class="controls mt-4">
          <div class="controls-inner">
            <div class="d-flex align-items-center gap-2">
              <span class="control-label">Plans</span>
            </div>
            <div class="searchbox">
              <i class="ti ti-search"></i>
              <input
                v-model="search"
                class="form-control"
                placeholder="Search plans… (e.g. HR, Payroll, CRM)"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="card soft-card">
        <div
          class="card-body py-5 d-flex justify-content-center align-items-center"
        >
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>

      <!-- Empty -->
      <div v-else-if="!filteredPlans.length" class="card soft-card">
        <div class="card-body py-5 text-center">
          <div class="empty-icon mb-3"><i class="ti ti-file-off"></i></div>
          <h5 class="mb-2">No plans found</h5>
          <p class="text-muted mb-0">
            Try a different search, or check back after plans are configured.
          </p>
        </div>
      </div>

      <!-- Plans grid -->
      <div v-else class="row g-4">
        <div
          v-for="plan in filteredPlans"
          :key="plan.id"
          class="col-xl-3 col-lg-4 col-md-6 col-sm-12 d-flex"
        >
          <div
            class="plan-card w-100"
            :class="{
              featured: plan.is_recommended,
              preselected: preselectedPlanId && preselectedPlanId === plan.id,
            }"
          >
            <!-- Header -->
            <div class="plan-header">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <div class="plan-name">
                    {{ plan.name }}
                    <span
                      v-if="plan.is_recommended"
                      class="badge badge-featured ms-2"
                    >
                      Recommended
                    </span>
                  </div>

                  <div v-if="isContactSalesPlan(plan)" class="plan-price mt-2">
                    <span class="amount">Contact Sales</span>
                    <div class="subtext text-muted mt-1">
                      Custom commercial terms for your organization.
                    </div>
                  </div>
                  <div v-else class="plan-price mt-2">
                    <span class="amount">
                      {{ currencySymbol(plan.currency)
                      }}{{ displayPrice(plan) }}
                    </span>
                  </div>

                  <div
                    v-if="
                      !isContactSalesPlan(plan) &&
                      hasDiscount(plan)
                    "
                    class="small text-success fw-semibold mt-1"
                  >
                    Intro offer saves {{ currencySymbol(plan.currency) }}{{ planDiscountAmount(plan) }}
                  </div>

                  <div class="subtext text-muted mt-1">
                    Best for teams that want
                    <span class="fw-semibold">connected workflows</span> across
                    modules.
                  </div>
                </div>

                <div class="plan-icon">
                  <i
                    class="ti"
                    :class="plan.is_recommended ? 'ti-crown' : 'ti-box'"
                  ></i>
                </div>
              </div>
            </div>

            <!-- Features -->
            <div class="plan-body">
              <div class="section-title">
                <i class="ti ti-checks me-2"></i>
                {{ isContactSalesPlan(plan) ? "What's included" : "Plan highlights" }}
              </div>

              <div class="feature-chips">
                <template v-if="featureCategoriesForPlan(plan).length">
                  <span
                    v-for="cat in featureCategoriesForPlan(plan)"
                    :key="cat"
                    class="chip"
                  >
                    <i class="ti ti-discount-check-filled me-1"></i>
                    {{ cat }}
                  </span>
                </template>

                <div v-else class="text-muted small d-flex align-items-center">
                  <i class="ti ti-alert-triangle me-2"></i>
                  No modules configured for this plan.
                </div>
              </div>

              <div class="mini-note mt-3">
                <template v-if="isContactSalesPlan(plan)">
                  <i class="ti ti-briefcase me-2"></i>
                  Sales-assisted onboarding and commercial setup.
                </template>
                <template v-else>
                  <i class="ti ti-sparkles me-2"></i>
                  Trial starts immediately after selection.
                </template>
              </div>
            </div>

            <!-- CTA -->
            <div class="plan-footer">
              <button
                class="btn w-100 btn-choose"
                :class="plan.is_recommended ? 'btn-primary' : 'btn-dark'"
                :disabled="submitting && selectedPlanId === plan.id"
                @click="handleChoosePlan(plan)"
              >
                <span
                    v-if="submitting && selectedPlanId === plan.id"
                    class="spinner-border spinner-border-sm me-2"
                  ></span>
                  Choose {{ plan.name }}
                  <i class="ti ti-arrow-right ms-2"></i>
              </button>

                <div class="tiny text-muted mt-2">
                <template v-if="isContactSalesPlan(plan)">
                  <i class="ti ti-phone-call me-1"></i> Contact sales for pricing and activation
                </template>
                <template v-else>
                <i class="ti ti-lock me-1"></i> Secure checkout later · No card
                required now
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="modal fade dq-sales-modal"
      id="contact_sales_modal_select_plan"
      tabindex="-1"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content dq-sales-card">
          <div class="modal-header dq-sales-header">
            <div class="d-flex align-items-start gap-3">
              <span class="dq-sales-icon">
                <i class="ti ti-briefcase"></i>
              </span>
              <div class="flex-grow-1">
                <h5 class="modal-title mb-1">Contact Sales</h5>
                <div class="dq-sales-subtitle">
                  <span v-if="selectedSalesPlan">
                    {{ selectedSalesPlan.name }} is available through a tailored sales engagement.
                  </span>
                  <span v-else>
                    This plan is available through a tailored sales engagement.
                  </span>
                </div>
              </div>
            </div>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>

          <div class="modal-body dq-sales-body">
            <div class="dq-sales-hero mb-4">
              <div class="dq-sales-eyebrow mb-2">Enterprise Plan</div>
              <h4 class="mb-2">Let's structure the right package for your organization.</h4>
              <p class="text-muted mb-0">
                Contact our sales team for pricing, rollout planning, and commercial terms.
              </p>
            </div>

            <div class="row g-3">
              <div class="col-md-6">
                <a class="dq-sales-contact text-decoration-none" href="mailto:info@donequick-technologicalworld.com">
                  <span class="dq-sales-contact-icon">
                    <i class="ti ti-mail"></i>
                  </span>
                  <span>
                    <span class="dq-sales-contact-label">Email</span>
                    <strong>info@donequick-technologicalworld.com</strong>
                  </span>
                </a>
              </div>
              <div class="col-md-6">
                <a class="dq-sales-contact text-decoration-none" href="tel:0720263910">
                  <span class="dq-sales-contact-icon">
                    <i class="ti ti-phone-call"></i>
                  </span>
                  <span>
                    <span class="dq-sales-contact-label">Phone</span>
                    <strong>0720263910</strong>
                  </span>
                </a>
              </div>
            </div>

            <div class="mini-note mt-4">
              <i class="ti ti-info-circle me-2"></i>
              Enterprise plans are handled directly by sales instead of self-service signup.
            </div>
          </div>

          <div class="modal-footer dq-sales-footer">
            <button
              type="button"
              class="btn btn-outline-secondary px-4"
              data-bs-dismiss="modal"
            >
              Close
            </button>
            <a class="btn btn-dark px-4" href="mailto:info@donequick-technologicalworld.com">
              Email Sales <i class="ti ti-arrow-up-right ms-2"></i>
            </a>
            <a class="btn btn-primary px-4" href="tel:0720263910">
              Call Sales <i class="ti ti-phone ms-2"></i>
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- background blobs -->
    <div class="blob blob-1"></div>
    <div class="blob blob-2"></div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Modal } from "bootstrap";
import publicPlanService from "@/services/public-plan.service";
import selfSubscriptionService from "@/services/self-subscription.service";
import { useAccessStore } from "@/stores/accessStore";
import { toast } from "@/utils/toast";
import { useAuthStore } from "@/stores/authStore";
import {
  getPlanDiscountAmount,
  getPlanDisplayPrice,
  hasPlanDiscount,
} from "@/utils/planPricing";

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();

const plans = ref([]);
const loading = ref(false);
const submitting = ref(false);
const selectedPlanId = ref(null);
const selectedSalesPlan = ref(null);
const access = useAccessStore();

const search = ref("");

// preselected from query
const preselectedPlanId = computed(() => {
  const v = Number(route.query.plan_id || 0);
  return v || null;
});

const safeDashboard = computed(() => {
  const roles = auth.user?.roles || [];
  const isSuperAdmin = roles.includes("SuperAdmin");
  const isAdmin = roles.includes("Admin");
  const isHr = roles.includes("Hr") || roles.includes("HR");
  const isEmployee = roles.includes("Employee");
  const isPureEmployee = isEmployee && !isAdmin && !isHr && !isSuperAdmin;

  if (isSuperAdmin) return "/super-admin/dashboard";
  return isPureEmployee
    ? "/dashboard/employee-dashboard"
    : "/dashboard/admin-dashboard";
});

// load plans
let contactSalesModalInstance = null;

onMounted(async () => {
  loading.value = true;
  try {
    const res = await publicPlanService.getAll();
    plans.value = Array.isArray(res.data) ? res.data : res.data || [];

  } catch (e) {
    console.error(e);
    toast("Failed to load plans", "error");
  } finally {
    loading.value = false;
  }

  const salesEl = document.getElementById("contact_sales_modal_select_plan");
  if (salesEl) {
    contactSalesModalInstance = new Modal(salesEl, { backdrop: "static" });
  }
});

// keep your sorting logic (unchanged)
const sortedPlans = computed(() => {
  return [...plans.value].sort((a, b) => {
    const pa = a.position ?? 999;
    const pb = b.position ?? 999;
    if (pa !== pb) return pa - pb;
    return (a.id || 0) - (b.id || 0);
  });
});

// ✅ filter by billing + search (UX upgrade)
const filteredPlans = computed(() => {
  const q = search.value.trim().toLowerCase();

  return sortedPlans.value
    .filter((p) => {
      // keep your current display conversion (so no strict filter needed),
      // but it still feels nicer to “focus” the selection:
      // show all plans, but search helps.
      if (!q) return true;

      const cats = featureCategoriesForPlan(p).join(" ").toLowerCase();
      const name = String(p.name || "").toLowerCase();
      return name.includes(q) || cats.includes(q);
    })
    .sort((a, b) => {
      // Put preselected or recommended towards the top visually
      const aBoost =
        (preselectedPlanId.value && a.id === preselectedPlanId.value
          ? -10
          : 0) + (a.is_recommended ? -5 : 0);
      const bBoost =
        (preselectedPlanId.value && b.id === preselectedPlanId.value
          ? -10
          : 0) + (b.is_recommended ? -5 : 0);
      return aBoost - bBoost;
    });
});

const priceForPlan = (plan) => getPlanDisplayPrice(plan);

const formatPrice = (value) =>
  new Intl.NumberFormat("en-KE", {
    minimumFractionDigits: Number(value) % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const displayPrice = (plan) => formatPrice(priceForPlan(plan));

const isContactSalesPlan = (plan) => !!plan?.is_contact_sales;

const planDiscountAmount = (plan) => formatPrice(getPlanDiscountAmount(plan));

const hasDiscount = (plan) =>
  !isContactSalesPlan(plan) && hasPlanDiscount(plan);

const HIDDEN_PRICING_FEATURES = [
  "HRM & Payroll",
  "Loans & Savings",
  "Administration & Reporting",
  "Finance & Accounts",
  "Project Management",
  "Projects",
  "CRM",
  "Free trial available based on configured duration",
  "Initial support included free",
];

const supportDisplay = (plan) => {
  const months = Number(plan?.support_period_months || 0);
  if (!months) return "";
  return `Initial ${months} ${months === 1 ? "month" : "months"} support free`;
};

const trialDisplay = (plan) => {
  if (!plan?.allow_trial) return "";
  const value = Number(plan?.trial_interval_value || plan?.trial_days || 0);
  const unit = String(plan?.trial_interval_unit || "days").toLowerCase();
  if (!value) return "";
  const normalizedUnit = value === 1 ? unit.replace(/s$/, "") : unit;
  return `${value} ${normalizedUnit} free trial`;
};

const featureCategoriesForPlan = (plan) => {
  const features = Array.isArray(plan?.marketing_features)
    ? plan.marketing_features.filter(
        (item) => !HIDDEN_PRICING_FEATURES.includes(String(item || "").trim())
      )
    : [];

  const support = supportDisplay(plan);
  const trial = trialDisplay(plan);
  if (support) features.push(support);
  if (trial) features.push(trial);

  return features.filter(Boolean);
};

const currencySymbol = (code) => {
  if (!code) return "";
  const map = {
    USD: "$",
    EUR: "€",
    GBP: "£",
    KES: "KES ",
    UGX: "UGX ",
    TZS: "TZS ",
    RWF: "RWF ",
  };
  return map[code] ?? code + " ";
};

// const handleChoosePlan = async (plan) => {
//   submitting.value = true;
//   selectedPlanId.value = plan.id;
//   try {
//     await selfSubscriptionService.create({ plan_id: plan.id });
//     toast("Plan selected. Trial access activated.");
//     await access.fetchAccess({ force: true });
//     await router.push("/dashboard/admin-dashboard");
//   } catch (e) {
//     console.error(e);
//     toast("Failed to create subscription", "error");
//   } finally {
//     submitting.value = false;
//   }
// };

const handleChoosePlan = async (plan) => {
  if (isContactSalesPlan(plan)) {
    selectedSalesPlan.value = plan;
    contactSalesModalInstance?.show();
    return;
  }

  submitting.value = true;
  selectedPlanId.value = plan.id;

  try {
    const res = await selfSubscriptionService.create({ plan_id: plan.id });
    const subscription = res?.data?.data || res?.data || {};
    const needsBilling =
      subscription?.payment_status === "unpaid" &&
      subscription?.status !== "trialing";

    toast(
      needsBilling
        ? "Plan selected. Complete payment to finish billing."
        : "Plan selected successfully."
    );

    // ✅ force re-fetch access (avoid cached modules on same session)
    if (typeof access.fetchAccess === "function") {
      await access.fetchAccess({ force: true }); // <-- add force support in store
    } else if (typeof access.ensureAccess === "function") {
      // fallback
      await access.ensureAccess(true);
    }

    if (needsBilling) {
      await router.push("/billing");
      return;
    }

    await router.push(
      auth.user?.roles?.includes("SuperAdmin")
        ? "/super-admin/dashboard"
        : "/dashboard/admin-dashboard"
    );
  } catch (e) {
    console.error(e);
    toast("Failed to create subscription", "error");
  } finally {
    submitting.value = false;
  }
};
</script>

<style scoped>
/* Page background */
.plan-page {
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  background: radial-gradient(
      900px 380px at 15% 0%,
      rgba(59, 130, 246, 0.14),
      transparent 60%
    ),
    radial-gradient(
      900px 380px at 85% 10%,
      rgba(99, 102, 241, 0.12),
      transparent 60%
    ),
    #f8fafc;
}

/* background blobs */
.blob {
  position: absolute;
  filter: blur(45px);
  opacity: 0.32;
  pointer-events: none;
  z-index: 0;
}
.blob-1 {
  width: 420px;
  height: 420px;
  left: -160px;
  bottom: -160px;
  background: rgba(59, 130, 246, 0.55);
  border-radius: 999px;
}
.blob-2 {
  width: 520px;
  height: 520px;
  right: -220px;
  top: -220px;
  background: rgba(99, 102, 241, 0.55);
  border-radius: 999px;
}

.container {
  position: relative;
  z-index: 1;
}

/* Hero */
.hero-card {
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(2, 6, 23, 0.06);
  border-radius: 22px;
  padding: 22px;
  box-shadow: 0 28px 70px rgba(2, 6, 23, 0.1);
  backdrop-filter: blur(10px);
}

.hero-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 10px;
  border-radius: 999px;
  font-size: 0.85rem;
  background: rgba(2, 6, 23, 0.02);
  border: 1px solid rgba(2, 6, 23, 0.06);
  color: #334155;
}

/* Stepper */
.stepper {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.step {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 999px;
  font-weight: 650;
  font-size: 0.85rem;
  background: rgba(2, 6, 23, 0.03);
  border: 1px solid rgba(2, 6, 23, 0.06);
  color: #475569;
}
.step.done {
  background: rgba(34, 197, 94, 0.1);
  border-color: rgba(34, 197, 94, 0.18);
  color: #166534;
}
.step.active {
  background: rgba(37, 99, 235, 0.1);
  border-color: rgba(37, 99, 235, 0.18);
  color: #1d4ed8;
}

/* Controls */
.controls {
  margin-top: 12px;
}
.controls-inner {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border-radius: 18px;
  background: rgba(2, 6, 23, 0.02);
  border: 1px solid rgba(2, 6, 23, 0.06);
}

.control-label {
  font-weight: 700;
  color: #334155;
  margin-right: 4px;
}
.mini {
  font-size: 0.85rem;
  font-weight: 700;
  color: #64748b;
}
.mini.active {
  color: #0f172a;
}

/* Search */
.searchbox {
  position: relative;
  min-width: 260px;
}
.searchbox i {
  position: absolute;
  top: 50%;
  left: 10px;
  transform: translateY(-50%);
  color: #64748b;
}
.searchbox input {
  padding-left: 34px;
  border-radius: 14px;
  border-color: rgba(2, 6, 23, 0.12);
}
.searchbox input:focus {
  border-color: rgba(37, 99, 235, 0.55);
  box-shadow: 0 0 0 0.2rem rgba(37, 99, 235, 0.12);
}

/* Soft card */
.soft-card {
  border-radius: 22px;
  border: 1px solid rgba(2, 6, 23, 0.06);
  box-shadow: 0 22px 55px rgba(2, 6, 23, 0.08);
}

/* Empty */
.empty-icon {
  width: 56px;
  height: 56px;
  border-radius: 18px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(2, 6, 23, 0.03);
  border: 1px solid rgba(2, 6, 23, 0.06);
  color: #334155;
  font-size: 22px;
}

/* Plan card */
.plan-card {
  display: flex;
  flex-direction: column;
  border-radius: 22px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(2, 6, 23, 0.06);
  box-shadow: 0 22px 55px rgba(2, 6, 23, 0.08);
  transition: transform 0.14s ease, box-shadow 0.14s ease,
    border-color 0.14s ease;
}
.plan-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 28px 70px rgba(2, 6, 23, 0.12);
  border-color: rgba(37, 99, 235, 0.18);
}

/* Featured */
.plan-card.featured {
  border-color: rgba(37, 99, 235, 0.35);
  box-shadow: 0 30px 80px rgba(37, 99, 235, 0.18);
}
.plan-card.featured .plan-header {
  background: linear-gradient(
    135deg,
    rgba(37, 99, 235, 0.12),
    rgba(99, 102, 241, 0.1)
  );
}

/* Preselected */
.plan-card.preselected {
  outline: 3px solid rgba(245, 158, 11, 0.28);
  outline-offset: 2px;
}

/* Plan sections */
.plan-header {
  padding: 18px;
  border-bottom: 1px solid rgba(2, 6, 23, 0.06);
}
.plan-body {
  padding: 18px;
  flex: 1;
}
.plan-footer {
  padding: 18px;
  border-top: 1px solid rgba(2, 6, 23, 0.06);
}

.plan-name {
  font-weight: 800;
  font-size: 1.1rem;
  color: #0f172a;
}

.plan-price .amount {
  font-size: 1.8rem;
  font-weight: 900;
  letter-spacing: -0.02em;
}
.annual-license-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.06);
  border: 1px solid rgba(15, 23, 42, 0.08);
  color: #334155;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.plan-price .per {
  font-weight: 650;
}

.plan-icon {
  width: 42px;
  height: 42px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(2, 6, 23, 0.03);
  border: 1px solid rgba(2, 6, 23, 0.06);
  color: #334155;
  font-size: 18px;
}

.badge-featured {
  background: rgba(34, 197, 94, 0.12);
  border: 1px solid rgba(34, 197, 94, 0.18);
  color: #166534;
  font-weight: 800;
  padding: 6px 10px;
  border-radius: 999px;
}

.subtext {
  font-size: 0.9rem;
}

/* Features */
.section-title {
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
}
.feature-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 10px;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 650;
  background: rgba(2, 6, 23, 0.02);
  border: 1px solid rgba(2, 6, 23, 0.06);
  color: #334155;
}
.chip i {
  color: rgba(34, 197, 94, 0.95);
}

.mini-note {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(2, 6, 23, 0.02);
  border: 1px solid rgba(2, 6, 23, 0.06);
  color: #334155;
  font-size: 0.92rem;
}

/* CTA */
.btn-choose {
  border-radius: 16px;
  box-shadow: 0 16px 40px rgba(2, 6, 23, 0.14);
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}
.btn-choose:hover:enabled {
  transform: translateY(-1px);
  box-shadow: 0 18px 46px rgba(2, 6, 23, 0.18);
}
.tiny {
  font-size: 0.82rem;
}

.dq-sales-card {
  border: 0;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 28px 70px rgba(2, 6, 23, 0.16);
}
.dq-sales-header {
  padding: 18px 18px 12px;
  border-bottom: 1px solid rgba(2, 6, 23, 0.06);
  background:
    radial-gradient(
      220px 120px at 0% 0%,
      rgba(16, 185, 129, 0.12),
      transparent 70%
    ),
    radial-gradient(
      240px 140px at 100% 0%,
      rgba(59, 130, 246, 0.14),
      transparent 65%
    ),
    rgba(255, 255, 255, 0.98);
}
.dq-sales-icon {
  width: 46px;
  height: 46px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(16, 185, 129, 0.12);
  border: 1px solid rgba(16, 185, 129, 0.22);
  color: #047857;
  font-size: 1.15rem;
  flex: 0 0 auto;
}
.dq-sales-subtitle {
  color: #64748b;
  font-size: 0.95rem;
}
.dq-sales-body {
  padding: 18px;
}
.dq-sales-hero {
  padding: 18px;
  border-radius: 20px;
  background:
    linear-gradient(
      135deg,
      rgba(16, 185, 129, 0.12),
      rgba(59, 130, 246, 0.08)
    ),
    #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
}
.dq-sales-eyebrow {
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #0f766e;
}
.dq-sales-contact {
  height: 100%;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(255, 255, 255, 0.96);
  color: #0f172a;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.dq-sales-contact:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.08);
}
.dq-sales-contact-icon {
  width: 48px;
  height: 48px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.18);
  color: #1d4ed8;
  font-size: 1.1rem;
  flex: 0 0 auto;
}
.dq-sales-contact-label {
  display: block;
  margin-bottom: 2px;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
}
.dq-sales-footer {
  padding: 16px 18px;
  border-top: 1px solid rgba(2, 6, 23, 0.06);
  background: rgba(2, 6, 23, 0.02);
  gap: 4px;
}

/* Footer note */
.footer-note .note-card {
  display: flex;
  align-items: center;
  padding: 12px 14px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(2, 6, 23, 0.06);
  color: #334155;
  box-shadow: 0 18px 40px rgba(2, 6, 23, 0.06);
}

/* misc */
.form-check-input {
  cursor: pointer;
}
</style>
